import { useState } from 'react';

import StripeCardModal from '@/components/stripe/StripeCardModal';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, ExternalLink, Loader2, Sparkles, Zap } from 'lucide-react';
import { toast } from 'sonner';

// ─── Types ───────────────────────────────────────────────────────

type Interval = 'monthly' | 'annual';

export interface SubscriptionPlanConfig {
  /** Nom du plan premium affiché */
  planName: string;
  /** Description courte du plan */
  description: string;
  /** Prix mensuel affiché (ex: "1" pour 1€/mois) */
  priceMonthly: string;
  /** Prix annuel affiché (ex: "10" pour 10€/an) */
  priceAnnual: string;
  /** Liste des avantages du plan premium */
  features: string[];
  /** true si déjà abonné */
  isSubscribed: boolean;
  /** Fonction d'appel API pour créer un SetupIntent (collecte de carte) */
  onSetupIntent: () => Promise<{ clientSecret: string }>;
  /** Fonction d'appel API pour souscrire */
  onSubscribe: (
    interval: Interval,
    paymentMethodId: string,
  ) => Promise<{ subscriptionId: string; status: string; clientSecret?: string }>;
  /** Fonction d'appel API pour le billing portal (null si pas d'abonnement actif) */
  onBillingPortal: (() => Promise<{ portalUrl: string }>) | null;
  /** Couleur d'accent (orange, emerald, blue) */
  accentColor: 'orange' | 'emerald' | 'blue';
  /** Query keys à invalider après souscription */
  invalidateKeys?: readonly (readonly unknown[])[];
  /** Si renseigné, le bouton Souscrire est désactivé et ce texte d'aide est affiché (ex. gating SIREN D6) */
  disabledReason?: string | null;
}

// ─── Couleurs par thème ──────────────────────────────────────────

const COLORS = {
  orange: {
    badge: 'bg-orange-100 text-orange-700',
    icon: 'bg-orange-100 text-orange-600',
    button: 'bg-orange-600 hover:bg-orange-700',
    ring: 'ring-orange-200',
    check: 'text-orange-500',
    activeBadge: 'bg-orange-100 text-orange-700 border-orange-200',
  },
  emerald: {
    badge: 'bg-emerald-100 text-emerald-700',
    icon: 'bg-emerald-100 text-emerald-600',
    button: 'bg-emerald-600 hover:bg-emerald-700',
    ring: 'ring-emerald-200',
    check: 'text-emerald-500',
    activeBadge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  blue: {
    badge: 'bg-blue-100 text-blue-700',
    icon: 'bg-blue-100 text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-700',
    ring: 'ring-blue-200',
    check: 'text-blue-500',
    activeBadge: 'bg-blue-100 text-blue-700 border-blue-200',
  },
};

// ─── Component ───────────────────────────────────────────────────

export default function SubscriptionPanel({ config }: { config: SubscriptionPlanConfig }) {
  const [interval, setInterval] = useState<Interval>('monthly');
  const [showCardModal, setShowCardModal] = useState(false);
  const queryClient = useQueryClient();
  const colors = COLORS[config.accentColor];

  const portalMutation = useMutation({
    mutationFn: () => config.onBillingPortal!(),
    onSuccess: ({ portalUrl }) => {
      window.open(portalUrl, '_blank');
    },
    onError: (err: Error) => {
      toast.error(err.message || "Impossible d'ouvrir le portail de facturation");
    },
  });

  // ─── Subscribed state ──────────────────────────────────────────

  if (config.isSubscribed) {
    return (
      <div className='space-y-4 max-w-2xl'>
        <div className='bg-white rounded-2xl p-6 border border-slate-200'>
          <div className='flex items-center gap-3 mb-4'>
            <div className={`w-10 h-10 ${colors.icon} rounded-lg flex items-center justify-center`}>
              <Sparkles className='w-5 h-5' />
            </div>
            <div>
              <h3 className='font-bold text-slate-900'>Abonnement actif</h3>
              <p className='text-sm text-slate-500'>Vous beneficiez du plan {config.planName}</p>
            </div>
            <span className={`ml-auto text-xs font-bold px-3 py-1 rounded-full border ${colors.activeBadge}`}>
              Actif
            </span>
          </div>

          <p className='text-sm text-slate-600 mb-5 leading-relaxed'>{config.description}</p>

          {config.onBillingPortal && (
            <button
              type='button'
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              className='cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-xl hover:bg-slate-800 transition disabled:opacity-50'
            >
              {portalMutation.isPending ? (
                <Loader2 className='w-4 h-4 animate-spin' />
              ) : (
                <ExternalLink className='w-4 h-4' />
              )}
              Gerer mon abonnement
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Not subscribed — upgrade offer ────────────────────────────

  const price = interval === 'monthly' ? config.priceMonthly : config.priceAnnual;
  const period = interval === 'monthly' ? '/mois' : '/an';

  return (
    <div className='space-y-6 max-w-2xl'>
      {/* Plan card */}
      <div className={`bg-white rounded-2xl p-6 border border-slate-200 ring-1 ${colors.ring}`}>
        <div className='flex items-center gap-3 mb-4'>
          <div className={`w-10 h-10 ${colors.icon} rounded-lg flex items-center justify-center`}>
            <Zap className='w-5 h-5' />
          </div>
          <div>
            <h3 className='font-bold text-slate-900'>{config.planName}</h3>
            <p className='text-sm text-slate-500'>{config.description}</p>
          </div>
        </div>

        {/* Features */}
        <ul className='space-y-2 mb-6'>
          {config.features.map(feature => (
            <li key={feature} className='flex items-start gap-2 text-sm text-slate-700'>
              <CreditCard className={`w-4 h-4 mt-0.5 shrink-0 ${colors.check}`} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* Interval toggle */}
        <div className='flex items-center gap-2 mb-5'>
          <button
            type='button'
            onClick={() => setInterval('monthly')}
            className={`cursor-pointer px-4 py-2 text-sm font-semibold rounded-lg transition ${
              interval === 'monthly' ? `${colors.badge}` : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            Mensuel
          </button>
          <button
            type='button'
            onClick={() => setInterval('annual')}
            className={`cursor-pointer px-4 py-2 text-sm font-semibold rounded-lg transition ${
              interval === 'annual' ? `${colors.badge}` : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            Annuel
          </button>
        </div>

        {/* Price + Subscribe */}
        <div className='flex items-end gap-4'>
          <div>
            <span className='text-3xl font-extrabold text-slate-900'>{price}&euro;</span>
            <span className='text-sm text-slate-500 ml-1'>{period}</span>
          </div>
          <button
            type='button'
            onClick={() => setShowCardModal(true)}
            disabled={!!config.disabledReason}
            className={`cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 ${colors.button} text-white text-sm font-semibold rounded-xl transition disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <CreditCard className='w-4 h-4' />
            Souscrire
          </button>
        </div>

        {config.disabledReason && <p className='mt-3 text-xs font-semibold text-amber-700'>{config.disabledReason}</p>}
      </div>

      <StripeCardModal
        open={showCardModal}
        onClose={() => setShowCardModal(false)}
        interval={interval}
        planLabel={`${config.planName} ${interval === 'monthly' ? 'Mensuel' : 'Annuel'}`}
        onSetupIntent={config.onSetupIntent}
        onSubscribe={async paymentMethodId => {
          const result = await config.onSubscribe(interval, paymentMethodId);
          return { clientSecret: result.clientSecret };
        }}
        onSuccess={() => {
          toast.success(`Abonnement ${config.planName} active !`);
          // Laisser le temps au webhook Stripe de mettre à jour la DB
          setTimeout(() => {
            void useAuthStore.getState().refreshSession();
            queryClient.invalidateQueries({ queryKey: queryKeys.me() });
            for (const key of config.invalidateKeys ?? []) {
              queryClient.invalidateQueries({ queryKey: key });
            }
          }, 3000);
        }}
      />
    </div>
  );
}
